import { useEffect, useRef, useState } from 'react';
import { createEmptyGraph } from '../../../core/entities/graph-document';
import type { ConnectionStrategy } from '../../../core/ports/preferences-repository';
import type { AccessMode } from '../../../core/policies/access-gate/preview-node-entitlement';
import { ConnectionStrategySwitcher } from '../../preferences/components/ConnectionStrategySwitcher';
import { useConnectionStrategy } from '../../preferences/connection-strategy/ConnectionStrategyProvider';
import { LanguageSwitcher } from '../../preferences/components/LanguageSwitcher';
import { useLocale } from '../../preferences/i18n/LocaleProvider';
import {
  CHRISTIAN_BIBLE_PUBLIC_PATH,
  CHRISTIAN_BIBLE_SAMPLE_ARCHIVE_FILENAME,
  JIN_YONG_SAMPLE_ARCHIVE_FILENAME,
  THREE_KINGDOMS_PUBLIC_PATH,
  THREE_KINGDOMS_SAMPLE_ARCHIVE_FILENAME,
  importPreparedArchiveIntoGraphStore,
  prepareGraphArchiveImport,
} from '../../archive-transfer/services/archive-graph-loader';
import { useGraphStore } from '../../graph-view/services/graph-store';
import {
  authConfigFromEnv,
  createWorkerArchiveSigningClient,
  fetchProtectedArchive,
  loadGoogleIdentityScript,
  renderGoogleSignInButton,
  unlockWithGoogleCredential,
  type FullModeAuthSession,
} from '../services/google-auth-unlock-service';

type StartupAction = 'three-kingdoms' | 'christian-bible' | 'jin-yong' | 'user' | null;
type TemplateSource = Exclude<StartupAction, 'user' | null>;
type AuthStatus = 'idle' | 'loading' | 'authenticating' | 'unconfigured' | 'not-enabled' | 'verify';
const AUTHOR_BLOG_URL = 'https://yicheng-yvonne.com/';

export function StartupProjectScreen({
  accessMode,
  authSession,
  onAuthSessionAccepted,
  onAuthSessionExpired,
  onReady,
}: {
  accessMode: AccessMode;
  authSession: FullModeAuthSession | null;
  onAuthSessionAccepted: (session: FullModeAuthSession) => void;
  onAuthSessionExpired: () => void;
  onReady: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const replaceGraph = useGraphStore((state) => state.replaceGraph);
  const setGraphConnectionStrategy = useGraphStore((state) => state.setConnectionStrategy);
  const [loadingAction, setLoadingAction] = useState<StartupAction>(null);
  const [error, setError] = useState<{ source: 'three-kingdoms' | 'christian-bible' | 'jin-yong' | 'user'; detail: string } | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('idle');
  const [aboutOpen, setAboutOpen] = useState(false);
  const { locale, setLocale, t } = useLocale();
  const { connectionStrategy, setConnectionStrategy } = useConnectionStrategy();
  const authConfig = authConfigFromEnv();

  useEffect(() => {
    setGraphConnectionStrategy(connectionStrategy);
  }, [connectionStrategy, setGraphConnectionStrategy]);

  useEffect(() => {
    if (accessMode !== 'preview') {
      return;
    }

    if (!authConfig) {
      setAuthStatus('unconfigured');
      return;
    }

    let active = true;
    setAuthStatus('loading');

    void loadGoogleIdentityScript()
      .then(() => {
        if (!active || !googleButtonRef.current) {
          return;
        }

        renderGoogleSignInButton({
          clientId: authConfig.clientId,
          container: googleButtonRef.current,
          locale,
          onCredential: (idToken) => {
            void handleGoogleCredential(idToken);
          },
        });
        setAuthStatus('idle');
      })
      .catch(() => {
        if (active) {
          setAuthStatus('verify');
        }
      });

    return () => {
      active = false;
    };
  }, [accessMode, authConfig?.clientId, authConfig?.workerUrl, locale]);

  function handleConnectionStrategyChange(nextConnectionStrategy: ConnectionStrategy) {
    setConnectionStrategy(nextConnectionStrategy);
    setGraphConnectionStrategy(nextConnectionStrategy);
  }

  async function handleGoogleCredential(idToken: string) {
    if (!authConfig) {
      setAuthStatus('unconfigured');
      return;
    }

    setAuthStatus('authenticating');

    try {
      const result = await unlockWithGoogleCredential(idToken, authConfig.workerUrl);
      if (result.success) {
        onAuthSessionAccepted(result);
        setAuthStatus('idle');
      } else {
        setAuthStatus('not-enabled');
      }
    } catch {
      setAuthStatus('verify');
    }
  }

  async function handleLoadTemplate(source: TemplateSource) {
    setLoadingAction(source);
    setError(null);

    try {
      let archiveBuffer: ArrayBuffer;

      if (!authSession || !authConfig) {
        onAuthSessionExpired();
        throw new Error('Full mode session is required.');
      }
      const signingClient = createWorkerArchiveSigningClient(authSession, authConfig.workerUrl);
      archiveBuffer = await fetchProtectedArchive(templateArchiveFilename(source), authSession, authConfig.workerUrl);

      const prepared = await prepareGraphArchiveImport(archiveBuffer, {
        requireSignedLargeArchives: false,
        signatureVerifier: signingClient,
      });
      await importPreparedArchiveIntoGraphStore(prepared, { replaceConfirmed: true });
      onReady();
    } catch (cause) {
      setLoadingAction(null);
      setError({ source, detail: extractArchiveErrorDetail(cause) });
    }
  }

  async function handleLoadPublicTemplate(source: 'three-kingdoms' | 'christian-bible') {
    setLoadingAction(source);
    setError(null);

    try {
      const publicPath = source === 'christian-bible' ? CHRISTIAN_BIBLE_PUBLIC_PATH : THREE_KINGDOMS_PUBLIC_PATH;
      const response = await fetch(publicPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch public template: ${response.status}`);
      }
      const archiveBuffer = await response.arrayBuffer();

      const prepared = await prepareGraphArchiveImport(archiveBuffer, {
        requireSignedLargeArchives: false,
      });
      await importPreparedArchiveIntoGraphStore(prepared, { replaceConfirmed: true });
      onReady();
    } catch (cause) {
      setLoadingAction(null);
      setError({ source, detail: extractArchiveErrorDetail(cause) });
    }
  }

  function handleStartBlank() {
    setError(null);
    replaceGraph(createEmptyGraph(t('blankProjectTitle')));
    onReady();
  }

  function handleOpenArchivePicker() {
    if (loadingAction || accessMode !== 'unlocked') {
      return;
    }

    setError(null);
    inputRef.current?.click();
  }

  async function handleArchiveSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setLoadingAction('user');
    setError(null);

    try {
      const signingClient = authSession && authConfig ? createWorkerArchiveSigningClient(authSession, authConfig.workerUrl) : undefined;
      const prepared = await prepareGraphArchiveImport(await new Response(file).arrayBuffer(), {
        requireSignedLargeArchives: false,
        signatureVerifier: signingClient,
      });
      await importPreparedArchiveIntoGraphStore(prepared, { replaceConfirmed: true });
      onReady();
    } catch (cause) {
      setLoadingAction(null);
      setError({ source: 'user', detail: extractArchiveErrorDetail(cause) });
    }
  }

  return (
    <main className="app-shell startup-shell">
      <section className="floating-panel startup-panel">
        <div className="startup-layout">
          <div className="startup-hero">
            <div className="startup-heading-group">
              <span className="startup-eyebrow">{accessMode === 'unlocked' ? t('startupUnlockedEyebrow') : t('startupPreviewEyebrow')}</span>
              <h1>{t('startupHeading')}</h1>
              <p>{t('startupBody')}</p>
            </div>
            <p className="startup-mode-note">{accessMode === 'unlocked' ? t('startupUnlockedMode') : t('startupPreviewMode')}</p>
            {accessMode === 'unlocked' && authSession && (
              <section aria-label={t('googleAccountPanelLabel')} className="startup-auth-card">
                <div className="startup-auth-card__identity">
                  <span>{t('googleSignedInAs')}</span>
                  <strong>{authSession.user.email}</strong>
                </div>
                <button className="hud-subtle-button startup-signout-button" onClick={onAuthSessionExpired} type="button">
                  <strong>{t('googleSignOut')}</strong>
                  <span>{t('googleSignOutDescription')}</span>
                </button>
              </section>
            )}
          </div>

          <div className="startup-controls">
            <div className="startup-preferences">
              <LanguageSwitcher locale={locale} onChange={setLocale} />
              <ConnectionStrategySwitcher connectionStrategy={connectionStrategy} onChange={handleConnectionStrategyChange} />
            </div>

            <section aria-labelledby="startup-template-title" className="startup-section">
              <div className="startup-section-heading">
                <h2 id="startup-template-title">{t('startupTemplatesTitle')}</h2>
                <span>{t('startupTemplatesHint')}</span>
              </div>

              <div className="startup-template-grid">
                <button
                  className="startup-template-button startup-template-button--primary"
                  type="button"
                  disabled={loadingAction !== null}
                  onClick={() => void (accessMode === 'unlocked' ? handleLoadTemplate('christian-bible') : handleLoadPublicTemplate('christian-bible'))}
                >
                  <span>{accessMode === 'unlocked' ? t('startupTemplateUnlocked') : t('startupTemplatePublic')}</span>
                  <strong>{loadingAction === 'christian-bible' ? t('startupLoadingTemplate') : t('startupLoadChristianBibleTemplate')}</strong>
                  <small>{t('startupLoadChristianBibleTemplateDescription')}</small>
                </button>

                <button
                  className="startup-template-button"
                  type="button"
                  disabled={loadingAction !== null}
                  onClick={() => void (accessMode === 'unlocked' ? handleLoadTemplate('three-kingdoms') : handleLoadPublicTemplate('three-kingdoms'))}
                >
                  <span>{accessMode === 'unlocked' ? t('startupTemplateUnlocked') : t('startupTemplatePublic')}</span>
                  <strong>{loadingAction === 'three-kingdoms' ? t('startupLoadingTemplate') : t('startupLoadThreeKingdomsTemplate')}</strong>
                  <small>{t('startupLoadThreeKingdomsTemplateDescription')}</small>
                </button>

                {accessMode === 'unlocked' && (
                  <button className="startup-template-button" type="button" disabled={loadingAction !== null} onClick={() => void handleLoadTemplate('jin-yong')}>
                    <span>{t('startupTemplateUnlocked')}</span>
                    <strong>{loadingAction === 'jin-yong' ? t('startupLoadingTemplate') : t('startupLoadJinYongTemplate')}</strong>
                    <small>{t('startupLoadJinYongTemplateDescription')}</small>
                  </button>
                )}
              </div>
            </section>

            <section aria-label={t('startupActionsTitle')} className="startup-action-strip">
              {accessMode === 'unlocked' && (
                <button className="hud-subtle-button" type="button" disabled={loadingAction !== null} onClick={handleOpenArchivePicker}>
                  <strong>{loadingAction === 'user' ? t('archiveImporting') : t('startupLoadArchive')}</strong>
                  <span>{t('startupLoadArchiveDescription')}</span>
                </button>
              )}

              <button className="hud-subtle-button" type="button" disabled={loadingAction !== null} onClick={handleStartBlank}>
                <strong>{t('startupBlankProject')}</strong>
                <span>{accessMode === 'unlocked' ? t('startupBlankProjectDescription') : t('startupBlankPreviewProjectDescription')}</span>
              </button>

              <button
                aria-controls="startup-about-panel"
                aria-expanded={aboutOpen}
                className="hud-subtle-button startup-about-toggle"
                onClick={() => setAboutOpen((current) => !current)}
                type="button"
              >
                {t('startupAbout')}
              </button>
            </section>

            {aboutOpen && (
              <section aria-labelledby="startup-about-title" className="startup-about-panel" id="startup-about-panel">
                <div>
                  <h2 id="startup-about-title">{t('startupAboutPanelTitle')}</h2>
                  <p>{t('startupAboutPanelBody')}</p>
                </div>
                <a className="hud-subtle-button" href={AUTHOR_BLOG_URL} rel="noreferrer" target="_blank">
                  {t('startupAboutBlog')}
                </a>
              </section>
            )}

            {accessMode === 'preview' && (
              <section aria-label="Access gate" className="startup-unlock-form">
                <div className="field-stack">
                  <span>{t('startupUnlockPrompt')}</span>
                  <div ref={googleButtonRef} className="google-signin-button" />
                </div>
                {authStatus === 'loading' && <p className="hud-hint" role="status">{t('googleSignInLoading')}</p>}
                {authStatus === 'authenticating' && <p className="hud-hint" role="status">{t('googleSignInVerifying')}</p>}
                {authStatus === 'unconfigured' && <p className="alert-text" role="alert">{t('googleSignInUnavailable')}</p>}
                {authStatus === 'not-enabled' && <p className="alert-text" role="alert">{t('googleAccountNotEnabled')}</p>}
                {authStatus === 'verify' && <p className="alert-text" role="alert">{t('unableToVerifyGoogleSignIn')}</p>}
              </section>
            )}

            {accessMode === 'unlocked' && (
              <input
                ref={inputRef}
                aria-label={t('startupLoadArchive')}
                type="file"
                accept=".journeynodes"
                onChange={(event) => void handleArchiveSelected(event)}
                hidden
              />
            )}

            {error && <p className="alert-text" role="alert">{formatArchiveError(error, t)}</p>}
          </div>
        </div>
      </section>
    </main>
  );
}

function extractArchiveErrorDetail(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown archive error.';
}

function formatArchiveError(
  error: { source: 'three-kingdoms' | 'christian-bible' | 'jin-yong' | 'user'; detail: string },
  t: ReturnType<typeof useLocale>['t']
): string {
  return formatMessage(t(error.source === 'user' ? 'archiveImportFailed' : 'archiveSampleLoadFailed'), { detail: error.detail });
}

function templateArchiveFilename(source: TemplateSource): string {
  if (source === 'three-kingdoms') {
    return THREE_KINGDOMS_SAMPLE_ARCHIVE_FILENAME;
  }

  if (source === 'christian-bible') {
    return CHRISTIAN_BIBLE_SAMPLE_ARCHIVE_FILENAME;
  }

  return JIN_YONG_SAMPLE_ARCHIVE_FILENAME;
}

function formatMessage(template: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}
